import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'
import moment from 'moment'

import * as gs from '../../../app/gStyles'

import {
  securityContract,
  securityContractLast,
  securityContractPreSignatureA,
  securityContractPreSignatureB,
  securityContractPreSignatureC,
} from '../../constants'

import ContractDetailRow from './ContractDetailRow'
import ContractLogo from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './SecurityContract.module.css'

export type WildcatContractProps = c.r<{
  customerID: string
  isPDF: boolean
  toggleSecuritySignatureModalCompany(): void
  toggleSecuritySignatureModalCustomer(): void
}>

export default React.memo<WildcatContractProps>(function WildcatContract({
  customerID,
  isPDF,
  toggleSecuritySignatureModalCompany,
  toggleSecuritySignatureModalCustomer,
}) {
  //#region styling
  //#endregion
  //#region global
  const { contractCompanyFull } = r.useContractCompany(customerID)

  const totalCost = r.useTotalCost('customer', customerID)

  const [customerData] = r.useCustomer(customerID)

  const {
    customerAddress,
    customerEmail,
    customerName,
    customerPhone,
    globalNotes,
    homeRep,
    security_cams,
    security_cams_show,
    security_system,
    security_system_show,
    security_tv,
    security_tv_show,

    security_contract_sign_company,
    security_contract_sign_company_date,
    security_contract_sign_customer,
    security_contract_sign_customer_date,
    solarCompany,
  } = customerData

  const { rows: _secCamRows } = r.useRowable<c.CamGroup>(
    customerID,
    'security_cams_detail',
  )
  const secCamRows =
    security_cams === 'no' || security_cams_show === 0
      ? c.EMPTY_ARRAY
      : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secSysRows } = r.useRowable<c.SecSysGroup>(
    customerID,
    'security_system_detail',
  )
  const secSysRows =
    security_system === 'no' || security_system_show === 0
      ? c.EMPTY_ARRAY
      : _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)
  const { rows: _secTVRows } = r.useRowable<c.SecurityTV>(
    customerID,
    'security_tv_detail',
  )
  const secTVRows =
    security_tv === 'no' || security_tv_show === 0
      ? c.EMPTY_ARRAY
      : _secTVRows.filter((ssg) => !!ssg.type)

  const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
    customerID,
    'security_contract_co_buyer',
  )
  const [financingAmt, , financingAmtHandle] = r.useCustomerFieldInput(
    customerID,
    'security_contract_financing_amt',
  )
  //#endregion
  //#region local
  //#endregion

  const buttonSignatureStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': { backgroundColor: c.companyColors[solarCompany] },
      width: 256,
    }),
    [solarCompany],
  )

  const activeCustomFields = React.useMemo(
    (): c.CustomerFields =>
      c
        .getFilledCustomFields(customerData)
        .filter(
          (field) => !!customerData[(field + '_show') as c.CustomerField],
        ),
    [customerData],
  )
  const activeCustomFieldsQties = React.useMemo(
    (): c.numbers => getCustomFieldsQties(activeCustomFields, customerData),
    [activeCustomFields, customerData],
  )
  const activeCustomFieldsNames = React.useMemo(
    (): c.strings => getCustomFieldsNames(activeCustomFields, customerData),
    [activeCustomFields, customerData],
  )
  const activeCustomFieldsCosts = React.useMemo(
    (): c.strings => getCustomFieldsCosts(activeCustomFields, customerData),
    [activeCustomFields, customerData],
  )
  const activeCustomFieldsNotes = React.useMemo(
    (): readonly c.strings[] =>
      getCustomFieldsNotes(activeCustomFields, customerData),
    [activeCustomFields, customerData],
  )

  return (
    <ReportPages companyBorder customerID={customerID} hPad>
      <div style={sx.logo}>
        <ContractLogo isPDF={isPDF} size="m" />
      </div>

      {gapBig}

      <div>
        <p className={styles['heading']}>{contractCompanyFull.longName}</p>

        <p style={sx.companyInfo}>{contractCompanyFull.address1}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.address2}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.email}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.phoneNumber}</p>
      </div>

      {gapMedium}

      <p style={sx.node}>
        This is a Purchase Agreement ("PA") on this Date{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {security_contract_sign_customer || security_contract_sign_company
            ? moment(
                security_contract_sign_customer_date ||
                  security_contract_sign_company_date,
              ).format(c.readableDateFormat)
            : moment().format(c.readableDateFormat)}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        between {contractCompanyFull.longName} ("Company") and:
      </p>

      {gapMedium}

      <p style={sx.node}>
        Buyer:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerName}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Co-Buyer:{' '}
        <input
          className={styles['input-inline-wide']}
          disabled={
            !!security_contract_sign_customer ||
            !!security_contract_sign_company
          }
          onChange={coBuyerHandle}
          title="Co-Buyer"
          value={coBuyer}
        />
      </p>

      <p style={sx.node}>
        Property Address:{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerAddress}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      <p style={sx.node}>
        Home / Cell Home:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerPhone}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        E-mail:
        <span className={styles['underlined']}>
          {c.blankChar(8)}
          {customerEmail}
          {c.blankChar(8)}
        </span>
      </p>

      <p style={sx.node}>
        sometimes collectively referred to herein as the "Parties or
        individually a "Party".
      </p>

      {gapSmall}

      <p style={sx.node}>
        Rep Name:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {homeRep}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>{' '}
        Rep Phone:
        <span className={styles['underlined']}>
          {c.blankChar(8)}
          n/a
          {c.blankChar(8)}
        </span>
      </p>

      {gapMedium}

      <p className={styles['subheading']}>SCOPE OF WORK</p>

      {gapMedium}

      <div
        style={{
          flexDirection: 'row',
          display: 'flex',
          width: '100%',
          margin: 0,
        }}
      >
        <p style={{ fontWeight: 'bold', textAlign: 'center', width: '15%' }}>
          Quantity
        </p>

        <p style={{ fontWeight: 'bold', width: '55%' }}>Description</p>

        <p style={{ fontWeight: 'bold', textAlign: 'end', width: '15%' }}>
          Unit Cost
        </p>

        <p style={{ fontWeight: 'bold', textAlign: 'end', width: '15%' }}>
          Total Cost
        </p>
      </div>

      {secCamRows.map(({ id, notes, price, type, qty }) => (
        <ContractDetailRow
          description={type}
          key={id}
          notes={notes.split('\n')}
          qty={qty}
          price={price}
        />
      ))}

      {secSysRows.map(({ id, notes, price, type, qty }) => (
        <ContractDetailRow
          description={type}
          key={id}
          notes={notes.split('\n')}
          qty={qty}
          price={price}
        />
      ))}

      {secTVRows.map(({ id, price, type }) => (
        <ContractDetailRow
          description={type}
          key={id}
          notes={c.EMPTY_ARRAY}
          qty={1}
          price={price}
        />
      ))}

      {activeCustomFields.map((k, i) => (
        <ContractDetailRow
          description={activeCustomFieldsNames[i]!}
          key={k}
          notes={activeCustomFieldsNotes[i]!}
          price={
            c.parseFloating(activeCustomFieldsCosts[i]!) /
            activeCustomFieldsQties[i]!
          }
          qty={activeCustomFieldsQties[i]!}
        />
      ))}

      {gapSmall}

      <div style={gs.flowRootWidth100}>
        {false && (
          <>
            <p className={styles['subheading']}>ADDITIONAL NOTES</p>

            {gapSmall}

            {globalNotes.length === 0 ? (
              <p style={sx.companyInfo}>n/a</p>
            ) : (
              <ul style={sx.node}>
                {globalNotes
                  .split('\n')
                  .filter((_) => !!_)
                  .map((noteLine, i) => (
                    <li key={i}>{noteLine}</li>
                  ))}
              </ul>
            )}

            {gapBig}
          </>
        )}

        <p style={{ ...sx.node, textAlign: 'end' }}>
          <span style={sx.bold}>
            TOTAL: {c.blankChar(12)}
            {Intl.NumberFormat('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            }).format(totalCost)}
          </span>
        </p>

        {false && (
          <p style={sx.node}>
            <span style={sx.bold}>FINANCED AMOUNT: $ </span>
            <span className={styles['underlined']}>
              {c.blankChar(24)}
              {totalCost}
              {c.blankChar(24)}
            </span>
          </p>
        )}

        {false && (
          <p style={sx.node}>
            <span style={sx.bold}>FINANCING COMPANY: $ </span>
            <input
              className={styles['input-inline-wide']}
              disabled={
                !!security_contract_sign_customer ||
                !!security_contract_sign_company
              }
              onChange={financingAmtHandle}
              title="Financing amont"
              value={financingAmt}
            />
          </p>
        )}

        {gapBig}

        <p style={sx.boldCursive}>
          Disclaimer: If cost to complete work is more than approved financed
          amount then customer is responsible for remaining balance.
        </p>
      </div>
      {/* end flow-root */}

      {gapBig}

      <div style={gs.flowRootWidth100}>
        <p className={styles['subheading']}>CUSTOMER/OWNER'S RIGHT TO CANCEL</p>

        {gapMedium}

        <p style={sx.node}>
          If this Agreement was signed at a residence and you do not want the
          materials or services, you as the owner of the Property, may cancel
          this Agreement at any time prior to midnight of the third day after
          the date of this transaction, by providing written notice to the
          Company.
        </p>
      </div>

      {gapMedium}

      <div style={gs.flowRootWidth100}>
        <p className={styles['subheading']}>
          TERMS AND CONDITIONS OF SERVICE AGREEMENT
        </p>

        {gapMedium}

        <p style={sx.node}>
          <span style={sx.bold}>1. {securityContract[0]!.title}:</span>{' '}
          {securityContract[0]!.body}
        </p>

        {gapMedium}
      </div>

      {securityContract.slice(1).map(({ body, title }, i) => (
        <React.Fragment key={i}>
          <p style={sx.node}>
            <span style={sx.bold}>
              {i + 2}. {title}:
            </span>{' '}
            {body}
          </p>

          {gapMedium}
        </React.Fragment>
      ))}

      {/* This flow-root ensures signatures aren't awkwardly by themselves on
        a lone page, tying them together with the last paragraph of 
        conditions */}
      <div style={gs.flowRootWidth100}>
        <p style={sx.node}>
          <span style={sx.bold}>
            {securityContract.length + 1}. {securityContractLast.title}:
          </span>{' '}
          {securityContractLast.body}
        </p>

        {gapMedium}

        <p style={sx.node}>{securityContractPreSignatureA}</p>

        {gapMedium}

        <p style={sx.node}>{securityContractPreSignatureB}</p>

        {gapSmall}

        <p style={sx.node}>{securityContractPreSignatureC}</p>

        <div className={styles['signatures']}>
          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Property owner:</p>

              <span>{customerName}</span>
            </div>

            {gs.rowGap24}

            {security_contract_sign_customer && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={security_contract_sign_customer}
                  width={200}
                />
                <p>
                  {moment(security_contract_sign_customer_date).format(
                    'YYYY/MM/DD - hh:mma',
                  )}
                </p>
              </>
            )}

            <p>Address: {customerAddress}</p>

            {gs.rowGap24}

            {isPDF || security_contract_sign_customer ? (
              <div />
            ) : (
              <Button
                onClick={toggleSecuritySignatureModalCustomer}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Client Signature
              </Button>
            )}
          </div>

          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Contractor:</p>
              <span>{homeRep}</span>
            </div>

            {security_contract_sign_company && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={security_contract_sign_company}
                  width={200}
                />
                <p>
                  {moment(security_contract_sign_company_date).format(
                    'YYYY/MM/DD - hh:mma',
                  )}
                </p>
              </>
            )}

            {gs.rowGap24}

            <p className={styles['label-signature']}>
              Authorized Representative of
            </p>
            <p className={styles['label-signature']}>
              {contractCompanyFull.longName}
            </p>

            {gs.rowGap24}

            {isPDF || security_contract_sign_company ? (
              <div />
            ) : (
              <Button
                onClick={toggleSecuritySignatureModalCompany}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Authorized Signature
              </Button>
            )}
          </div>
        </div>
      </div>
    </ReportPages>
  )
})

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  margin: 0,
}
const sx = {
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'center' as const,
  },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: sxNode,
  bold: { ...sxNode, fontWeight: 'bold' },
  boldCursive: { ...sxNode, fontStyle: 'italic', fontWeight: 'bold' },
}

export const getCustomFieldsQties = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.numbers =>
  activeCustomFields.map(
    (field): number =>
      customerData[(field + '_qty') as c.CustomerField] as number,
  )

export const getCustomFieldsNames = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_name') as c.CustomerField] as string,
  )

export const getCustomFieldsCosts = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_customer_cost') as c.CustomerField] as string,
  )

export const getCustomFieldsNotes = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
) =>
  activeCustomFields.map((field): c.strings => {
    const notes = customerData[(field + '_notes') as c.CustomerField] as string

    return notes.split('\n').filter((_) => !!_)
  })
